import type { Namespace } from '../types';

// DE drafted — queued for native review. Location hierarchy (R-121): the coded
// 5-level location tree (Site → Building → Zone → Sub-zone → Asset position)
// that the W2 asset/incident/change operation hangs off.
export const locations = {
  title: { de: 'Standort-Hierarchie', en: 'Location Hierarchy' },
  collapseAll: { de: 'Alle einklappen', en: 'Collapse all' },
  expandAll: { de: 'Alle ausklappen', en: 'Expand all' },
  desc: {
    de: 'Der codierte Standort-Baum dieses Standorts — Gebäude, Zonen und Asset-Positionen mit Koordinaten, Zutritts-Kategorie und PSA.',
    en: 'This site’s coded location tree — buildings, zones and asset positions with coordinates, access category and PPE.',
  },

  statNodes: { de: 'Knoten', en: 'Nodes' },
  statBuildings: { de: 'Gebäude', en: 'Buildings' },
  statZones: { de: 'Zonen', en: 'Zones' },
  statPositions: { de: 'Asset-Positionen', en: 'Asset positions' },

  colLocation: { de: 'Standort', en: 'Location' },
  colCode: { de: 'Code', en: 'Code' },
  colType: { de: 'Typ', en: 'Type' },
  colAccess: { de: 'Zutritt', en: 'Access' },
  colCoords: { de: 'Koordinaten (m)', en: 'Coordinates (m)' },
  colPpe: { de: 'PSA', en: 'PPE' },
  colRobotNode: { de: 'Roboter-Knoten', en: 'Robot node' },
  colKind: { de: 'Raum-Art', en: 'Kind' },
  colActions: { de: 'Aktionen', en: 'Actions' },
  colStatus: { de: 'Status', en: 'Status' },

  // R-149 (D-114): per-kind labels now come from the DB taxonomy (`location_kinds`,
  // label_en/label_de), not hardcoded here. Only the column/field chrome remains.
  kindHint: {
    de: 'Eine Zone als Aussaat-, Keim-, Grow- oder Plumbing-Raum zu markieren legt automatisch ihren Mapper-Raum an.',
    en: 'Tagging a zone as a seeding, germination, grow or plumbing room auto-creates its mapper room.',
  },

  typeSite: { de: 'Standort', en: 'Site' },
  typeBuilding: { de: 'Gebäude', en: 'Building' },
  typeZone: { de: 'Zone', en: 'Zone' },
  typeSubzone: { de: 'Unterzone', en: 'Sub-zone' },
  typeAssetPosition: { de: 'Asset-Position', en: 'Asset position' },

  accessOpen: { de: 'Offen', en: 'Open' },
  accessHygiene: { de: 'Hygiene-Zone', en: 'Hygiene zone' },
  accessCold: { de: 'Kälte-Zone', en: 'Cold zone' },
  accessElectrical: { de: 'Elektro-Bereich', en: 'Electrical' },
  accessConfined: { de: 'Enger Raum', en: 'Confined space' },

  active: { de: 'Aktiv', en: 'Active' },
  retired: { de: 'Stillgelegt', en: 'Retired' },
  retire: { de: 'Stilllegen', en: 'Retire' },
  restore: { de: 'Reaktivieren', en: 'Restore' },
  retireTitle: { de: '{name} stilllegen?', en: 'Retire {name}?' },
  retireMsg: {
    de: '{code} verschwindet aus dem aktiven Betrieb (Asset-Zuordnung, Planung, Auto-Incidents). Reaktivieren ist jederzeit möglich.',
    en: '{code} disappears from active operation (asset placement, planning, auto-incidents). You can restore it at any time.',
  },
  showRetired: { de: 'Stillgelegte anzeigen', en: 'Show retired' },

  importCsv: { de: 'CSV importieren', en: 'Import CSV' },
  importTitle: { de: 'Standort-Import', en: 'Location import' },
  importDryRun: {
    de: 'Prüflauf — {applied} von {total} Zeilen gültig, {errors} Fehler.',
    en: 'Dry run — {applied} of {total} rows valid, {errors} errors.',
  },
  importApplied: {
    de: 'Import angewendet — {applied} von {total} Zeilen übernommen, {errors} Fehler.',
    en: 'Import applied — {applied} of {total} rows written, {errors} errors.',
  },
  importApply: { de: 'Import anwenden', en: 'Apply import' },
  importLine: { de: 'Zeile {line}: {error}', en: 'Line {line}: {error}' },
  importFailed: { de: 'Import fehlgeschlagen', en: 'Import failed' },

  // Create / edit form (R-121).
  newLocation: { de: 'Neuer Standort', en: 'New location' },
  editLocation: { de: 'Standort bearbeiten', en: 'Edit location' },
  create: { de: 'Anlegen', en: 'Create' },
  edit: { de: 'Bearbeiten', en: 'Edit' },
  // R-149 top-down builder: add a child under a specific tree row (parent locked).
  addChild: { de: 'Unterknoten', en: 'Add child' },
  addChildTitle: { de: 'Unterknoten zu {parent} hinzufügen', en: 'Add a child under {parent}' },
  autoRackNodeHint: {
    de: 'Eine Unterzone unter einem Grow/Plumbing Room legt automatisch ihr Rack/ihren Node im Mapper an.',
    en: 'A sub-zone under a grow/plumbing room auto-creates its rack/node in the mapper.',
  },

  // R-189: racks × levels per grow room. Rack and level counts differ per room
  // and are set here; the racks themselves are tree nodes, so generating them
  // lets R-149 auto-provision the mapper racks (the tree stays the SSOT).
  racksAction: { de: 'Racks & Ebenen', en: 'Racks & levels' },
  racksTitle: { de: 'Racks & Ebenen — {name}', en: 'Racks & levels — {name}' },
  racksIntro: {
    de: 'Rack- und Ebenen-Anzahl unterscheiden sich je Raum. Racks werden als Baum-Knoten angelegt; die Mapper-Racks entstehen daraus automatisch.',
    en: 'Rack and level counts differ per room. Racks are created as tree nodes, and the mapper racks are provisioned from them automatically.',
  },
  // R-354: racks are named, not coded — "Rack 01, Rack 02 …" is what an operator
  // reads on the shelf and on every screen that shows one, and this is also the
  // count the cloud's Capacity screen plans against. Say both: the naming, so
  // nobody expects to choose it, and the consequence, so adding racks here is
  // understood as changing what production planning may book.
  racksNaming: {
    de: 'Racks werden fortlaufend benannt — Rack 01, Rack 02 … — und in dieser Reihenfolge nummeriert. Die Anzahl hier ist zugleich die Rack-Zahl, mit der die Produktionsplanung diesen Raum belegt.',
    en: 'Racks are named in sequence — Rack 01, Rack 02 … — and numbered in that order. The number set here is also the rack count production planning books this room against.',
  },
  fRacks: { de: 'Racks in diesem Raum', en: 'Racks in this room' },
  fRacksHint: {
    de: '1 bis {max}. Erneutes Ausführen mit einer höheren Zahl fügt nur die fehlenden Racks hinzu.',
    en: '1 to {max}. Re-running with a higher number only adds the racks that are missing.',
  },
  fLayers: { de: 'Ebenen pro Rack', en: 'Levels per rack' },
  fLayersHint: {
    de: 'Nutzbare Ebenen auf jedem Rack. Ohne Angabe bleibt die Tiefe unerfasst — die Kapazität verweigert den Raum dann, statt zu raten.',
    en: 'Usable levels on every rack. Left empty the depth stays unrecorded, and capacity refuses the room rather than guessing.',
  },
  racksCurrent: {
    de: 'Aktuell: {racks} Racks · {layers} Ebenen pro Rack',
    en: 'Currently: {racks} racks · {layers} levels per rack',
  },
  racksLayersUnset: { de: 'nicht erfasst', en: 'not recorded' },
  racksPreview: { de: 'Vorschau', en: 'Preview' },
  racksApply: { de: 'Übernehmen', en: 'Apply' },
  racksPreviewResult: {
    de: '{created} anzulegen, {existing} bereits vorhanden.',
    en: '{created} to create, {existing} already there.',
  },
  racksApplied: {
    de: '{created} angelegt, {existing} bereits vorhanden.',
    en: '{created} created, {existing} already there.',
  },
  racksLayersSaved: { de: 'Ebenen pro Rack auf {layers} gesetzt.', en: 'Levels per rack set to {layers}.' },
  racksNoRoom: {
    de: 'Für diesen Standort ist noch kein Grow Room angelegt — Ebenen pro Rack können erst danach gesetzt werden.',
    en: 'No grow room is provisioned for this location yet, so levels per rack cannot be set until it is.',
  },
  racksFailed: { de: 'Racks konnten nicht angelegt werden', en: 'Could not generate the racks' },
  // The refusal names the offending value — repeating the hint told the operator nothing.
  racksRangeError: {
    de: '{value} liegt außerhalb von 1 bis {max} — bitte eine ganze Zahl in diesem Bereich angeben.',
    en: '{value} is outside 1 to {max} — enter a whole number in that range.',
  },
  layersPositiveError: {
    de: '{value} ist keine gültige Ebenen-Anzahl — mindestens 1, oder leer lassen, um die Tiefe unerfasst zu lassen.',
    en: '{value} is not a valid level count — use 1 or more, or leave it empty to keep the depth unrecorded.',
  },

  fParent: { de: 'Übergeordneter Standort', en: 'Parent' },
  fParentPlaceholder: { de: 'Übergeordneten Knoten wählen…', en: 'Select a parent node…' },
  fParentHint: {
    de: 'Kein übergeordneter Knoten legt einen neuen Standort (Ebene 1) an.',
    en: 'No parent creates a new top-level site (level 1).',
  },
  noParentSite: { de: 'Kein übergeordneter Knoten — neuer Standort', en: 'No parent — new site' },
  parentRequired: {
    de: 'Ein übergeordneter Knoten ist ab Ebene 2 erforderlich.',
    en: 'A parent is required below level 1.',
  },
  maxDepth: {
    de: 'Eine Asset-Position (Ebene 5) kann keine untergeordneten Knoten haben.',
    en: 'An asset position (level 5) cannot have children.',
  },

  fType: { de: 'Typ / Ebene', en: 'Type / level' },
  derivedType: {
    de: 'Unter {parent} → dies wird eine {type} (Ebene {level}).',
    en: 'Under {parent} → this will be a {type} (level {level}).',
  },
  derivedTypeSite: { de: 'Dies wird ein Standort (Ebene 1).', en: 'This will be a site (level 1).' },
  typeChoiceHint: {
    de: 'Asset-Position hängt direkt an der Zone; Unterzone gruppiert weitere Positionen darunter.',
    en: 'Asset position hangs directly off the zone; sub-zone groups further positions beneath it.',
  },

  fSegment: { de: 'Code-Segment', en: 'Code segment' },
  fSegmentPlaceholder: { de: 'z. B. GR07', en: 'e.g. GR07' },
  // The picker's empty option. An operator chooses WHAT the place is; the code
  // assembles itself from that and the parent.
  fSegmentChoose: { de: 'Art auswählen…', en: 'Choose what this is…' },
  segmentHint: {
    de: 'Vollständiger Code: {code}',
    en: 'Full code: {code}',
  },
  segmentTokens: {
    de: 'Gültige {type}-Segmente: {tokens}',
    en: 'Valid {type} segments: {tokens}',
  },
  segmentTokensNone: {
    de: 'Keine {type}-Tokens im Vokabular (Commons-Sync noch nicht angewendet).',
    en: 'No {type} tokens in the vocabulary (commons sync not yet applied).',
  },
  segmentInvalid: {
    de: 'Segment entspricht keinem gültigen {type}-Token.',
    en: 'Segment does not match a valid {type} token.',
  },
  segmentRequired: { de: 'Code-Segment ist erforderlich.', en: 'Code segment is required.' },

  // The code is generated from the name now, so the field carries the rule.
  nameCodeHint: {
    de: 'Der Code wird aus diesem Namen erzeugt — z. B. „Test Room 001" → TR_001.',
    en: 'The code is generated from this name — e.g. \u201cTest Room 001\u201d \u2192 TR_001.',
  },
  nameTaken: {
    de: 'Hier gibt es diesen Namen bereits ({code}) — zwei Orte unter demselben Elternteil können nicht denselben Namen tragen.',
    en: 'That name is already used here ({code}) — two places under one parent cannot share a name.',
  },
  zoneKindPlaceholder: { de: 'Raumart suchen…', en: 'Search room kinds…' },
  fStockroom: { de: 'Lagerort', en: 'Store' },
  stockroomLabel: { de: 'Dies ist ein Lagerort', en: 'This place is a store' },
  // Says what the flag DOES, because nothing else on the screen does: naming a
  // zone "Store Room" does not make it one, and asset intake refuses without it.
  stockroomHint: {
    de: 'Bestand kann hier eingelagert werden — Wareneingang, Umlagerungen und die Lagerübersicht lesen dieses Kennzeichen.',
    en: 'Stock can be received and held here — intake, transfers and the Warehouses screen all read this flag.',
  },
  // A capability, exactly like Stockroom above — a per-node fact, not a taxonomy
  // value, saved via its own endpoint after the node exists (item 27).
  fLightsOn: { de: 'Licht-an-Zeit', en: 'Lights-on time' },
  invalidTime: { de: 'Ungültige Uhrzeit (HH:MM).', en: 'Invalid time (HH:MM).' },
  // ⚠ R-356 — the field this belongs to is HIDDEN, and the sentence it used to
  // carry ("without it the room can never receive an automation plan") is retired
  // with the refusal behind it: a cycle is anchored on when its crop was started,
  // not on a room's minute of the day. The key stays with the dormant code path.
  lightsOnHint: {
    de: 'Informativ. Die Automatisierung nutzt diese Zeit nicht mehr — ein Zyklus zählt ab dem Start seiner Kultur.',
    en: 'Informational. Automation no longer reads it — a cycle counts from the moment its crop was started.',
  },
  lightsOnSaveFailed: {
    de: 'Der Ort wurde gespeichert, aber die Licht-an-Zeit konnte nicht übernommen werden.',
    en: 'The place saved, but the lights-on time could not be saved.',
  },
  // R-350 (D-175): which plumbing room's loop waters a grow room. Its own endpoint,
  // like Stockroom and Lights-on, so a CSV re-import cannot unmap a loop.
  fFedBy: { de: 'Versorgt von (Plumbing Room)', en: 'Fed by (plumbing room)' },
  fedByNone: { de: '— kein Kreislauf —', en: '— no loop —' },
  fedByHint: {
    de: 'Der Kreislauf, dessen Ventil diesen Raum bewässert. Bei mehreren Plumbing Rooms wird ohne Zuordnung die Bewässerung DIESES Raums nicht gefahren.',
    en: "The loop whose valve waters this room. With several plumbing rooms and no mapping, THIS room's irrigation is not driven.",
  },
  fedBySaveFailed: {
    de: 'Der Ort wurde gespeichert, aber der Kreislauf konnte nicht übernommen werden.',
    en: 'The place saved, but the fertigation loop could not be saved.',
  },
  colFedBy: { de: 'Versorgt von', en: 'Fed by' },
  // Examples on the create form, never a repeat of the label.
  phNameEn: { de: 'z. B. Nordhalle', en: 'e.g. North Hall' },
  phNameDe: { de: 'z. B. Nordhalle', en: 'e.g. Nordhalle' },
  phLightsOn: { de: 'z. B. 06:00', en: 'e.g. 06:00' },
  phCoord: { de: 'z. B. 12.5', en: 'e.g. 12.5' },
  phHeading: { de: 'z. B. 90', en: 'e.g. 90' },
  phRobotNode: { de: 'z. B. NODE-014', en: 'e.g. NODE-014' },
  phPpe: { de: 'z. B. Handschuhe, Schutzbrille', en: 'e.g. Gloves, safety glasses' },
  // Every location write reports its outcome. Retire and restore share one hook,
  // so the call site — which knows which it asked for — picks the message.
  toastLocationCreated: { de: '„{name}“ wurde angelegt.', en: '“{name}” created.' },
  toastLocationUpdated: { de: '„{name}“ wurde aktualisiert.', en: '“{name}” updated.' },
  toastLocationRetired: { de: '„{name}“ wurde stillgelegt.', en: '“{name}” retired.' },
  toastLocationRestored: { de: '„{name}“ wurde wiederhergestellt.', en: '“{name}” restored.' },
  toastLocationsImported: {
    de: '{n} Standorte importiert.',
    en: '{n} locations imported.',
  },
  toastRacksSaved: { de: 'Racks gespeichert.', en: 'Racks saved.' },
  fNameEn: { de: 'Name (EN)', en: 'Name (EN)' },
  fNameDe: { de: 'Name (DE)', en: 'Name (DE)' },
  nameRequired: { de: 'Name (EN) ist erforderlich.', en: 'Name (EN) is required.' },
  fAccess: { de: 'Zutritts-Kategorie', en: 'Access category' },
  fZoneKind: { de: 'Raum-Art', en: 'Room kind' },
  // ⚠ Shown only when the SELECTED kind is IoT-managed, so it must name every
  // managed kind — it listed two of four, and the germination and seeding rooms it
  // omitted are exactly the ones an operator is least sure about.
  zoneKindHint: {
    de: 'Aussaat-, Keim-, Grow- und Plumbing-Raum legen automatisch den zugehörigen Mapper-Raum an.',
    en: 'A seeding, germination, grow or plumbing room auto-provisions the matching mapper room.',
  },
  // R-239: the destructive direction, which had no wording at all.
  kindLeavingManaged: {
    de: '⚠ Diese Änderung ARCHIVIERT den IoT-Raum, den dieser Standort besitzt ({from}) — samt seiner Racks und deren Ebenen. Ist ein Mikrocontroller im Raum zugewiesen, wird die Änderung abgelehnt (409); zuerst das Gerät lösen.',
    en: '⚠ This ARCHIVES the IoT room this location owns ({from}) — along with that room\u2019s racks and their levels. If a microcontroller is assigned anywhere in the room the change is refused (409); unassign the device first.',
  },
  kindLeavingChildren: {
    de: 'Die {n} untergeordneten Knoten werden auf die neue Art neu abgeleitet.',
    en: 'Its {n} child node(s) will be re-derived for the new kind.',
  },

  advanced: { de: 'Erweitert (optional)', en: 'Advanced (optional)' },
  fCoordX: { de: 'X (m)', en: 'X (m)' },
  fCoordY: { de: 'Y (m)', en: 'Y (m)' },
  fCoordZ: { de: 'Z (m)', en: 'Z (m)' },
  fHeading: { de: 'Ausrichtung (°)', en: 'Heading (°)' },
  fRobotNode: { de: 'Roboter-Knoten-ID', en: 'Robot node ID' },
  fPpe: { de: 'Erforderliche PSA', en: 'Required PPE' },
  ppeHint: { de: 'Komma-getrennt', en: 'Comma-separated' },

  saveFailed: { de: 'Speichern fehlgeschlagen', en: 'Save failed' },

  retireBlocked: {
    de: 'Standort hat noch aktive Unter-Standorte.',
    en: 'Location still has active children.',
  },
  emptyTitle: { de: 'Noch keine Standorte', en: 'No locations yet' },
  emptyMsg: {
    de: 'Importieren Sie den Standort-Baum als CSV (Bulk-Import-Schema) oder legen Sie Knoten per API an.',
    en: 'Import the location tree as CSV (bulk-import schema) or create nodes via the API.',
  },
  loadError: { de: 'Standorte konnten nicht geladen werden', en: 'Failed to load locations' },
} satisfies Namespace;
